import i18next from 'i18next';
import React, { FC, memo, ReactElement, useMemo, useState } from 'react';
import { box } from 'tweetnacl';
import { decrypt, toChatDisplayText } from '../../util/crypto';
import { decodeBase64 } from 'tweetnacl-util';
import { formatTimeOrRelative } from '../../util/date-utils';
import { useChat } from '../../contexts/chat-context';
import BootstrapAvatar from '../BootstrapAvatar';
import SearchBar from '../SearchBar';
import EmptyState from '../EmptyState';
import { ChatHeadDto } from '../../../../shared/dtos/chat.dto';

type Props = {
  therapies: ChatHeadDto[];
  selectedTherapy: ChatHeadDto | null;
  handleSelectTherapy: (therapy: ChatHeadDto) => void;
};

const ChatHeadComponent: FC<Props> = ({ therapies, selectedTherapy, handleSelectTherapy }: Props) => {
  const { tKeyPair, chatHeadMap, unreadPatientIds } = useChat();
  const [searchTerm, setSearchTerm] = useState('');

  const visibleTherapies: ChatHeadDto[] = useMemo(() => {
    const term: string = searchTerm.toLowerCase();

    return therapies.filter((therapy: ChatHeadDto) =>
      `${therapy.patientFirstName} ${therapy.patientLastName}`.toLowerCase().includes(term));
  }, [therapies, searchTerm]);

  const changeChatHead = (therapy: ChatHeadDto): void => {
    if (therapy.id !== selectedTherapy?.id) {
      handleSelectTherapy(therapy);
    }
  };

  const renderLastMessage = (therapy: ChatHeadDto): ReactElement => {
    const patientKey: string = therapy.patientKey;
    const message: string | null = chatHeadMap.get(therapy.patientId)?.text ?? therapy.lastMessage?.text ?? null;

    if (message && patientKey && tKeyPair) {
      const finalKeyTherapist: Uint8Array = box.before(decodeBase64(patientKey), tKeyPair.secretKey);
      const actualMsg: string = toChatDisplayText(decrypt(finalKeyTherapist, message)).replace(/\n/g, ' ').replace(/<[^>]*>?/gm, '');
      return <div className="small text-muted text-truncate">{actualMsg}</div>;
    }

    return <div className="small"></div>;
  };

  const renderChatHead = (therapy: ChatHeadDto): ReactElement => {
    const name: string = `${therapy.patientFirstName} ${therapy.patientLastName}`;
    const date: string | undefined = chatHeadMap.get(therapy.patientId)?.createdAt ?? therapy.lastMessageDate;
    const isSelected: boolean = therapy.id === selectedTherapy?.id;

    return (
      <button
        type="button"
        key={therapy.id}
        className={`list-group-item list-group-item-action border-0 border-bottom px-3 py-3 ${isSelected ? 'bg-body-secondary' : ''}`}
        onClick={() => changeChatHead(therapy)}>
        <div className="d-flex align-items-center gap-3">
          <BootstrapAvatar
            name={name}
            size={40}
            color="#707089"
            className="rounded-circle flex-shrink-0"
            round={true}
          />
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex justify-content-between align-items-baseline gap-2">
              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                {unreadPatientIds.has(therapy.patientId) ?
                  <span className="rounded-circle flex-shrink-0" style={{ width: 8, height: 8, backgroundColor: '#ff695f' }} />
                  : null}
                <span className="fw-semibold text-truncate">{name}</span>
              </div>
              <span className="small text-muted flex-shrink-0">{date ? formatTimeOrRelative(date) : ''}</span>
            </div>
            {renderLastMessage(therapy)}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="d-flex flex-column flex-shrink-0 border-end overflow-hidden" style={{ width: 360 }}>
      <div className="p-3 border-bottom">
        <SearchBar onSearch={setSearchTerm} />
      </div>
      {visibleTherapies.length === 0 ?
        <div className="p-3">
          <EmptyState icon="fas fa-user" message={i18next.t('chat.noPatientFound')} />
        </div>
        : <div className="list-group list-group-flush" style={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto' }}>
          {visibleTherapies.map(renderChatHead)}
        </div>}
    </div>
  );
};

export default memo(ChatHeadComponent);
